import { ChangeDetectionStrategy, Component, computed, inject, input, ResourceRef, signal } from '@angular/core';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LightboxComponent } from '@components/lightbox/lightbox.component';
import type { WaterUserMediaItem } from '@models/water-user-media-batches';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/confirmation.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { WaterUserMediaService } from '@services/water-user-media.service';
import { extractYoutubeId, youtubeThumbnailUrl } from '@utils/youtube';
import { filter, firstValueFrom, type Observable } from 'rxjs';
import { WaterUserMediaEditFormComponent } from './water-user-media-edit-form.component';

type ApprovalFilter = 'approved' | 'not';

@Component({
  selector: 'app-water-user-media-grid',
  templateUrl: './water-user-media-grid.component.html',
  styleUrl: './water-user-media-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatButtonToggleModule, MatIconModule, MatTooltipModule, TranslatePipe],
})
export class WaterUserMediaGridComponent {
  private readonly mediaService = inject(WaterUserMediaService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly confirmation = inject(ConfirmationService);

  readonly waterId = input.required<number>();
  readonly batchId = input.required<number>();
  readonly selectedApproval = signal<ApprovalFilter>('approved');

  private readonly mediaResource: ResourceRef<WaterUserMediaItem[] | undefined> = rxResource({
    params: () => ({ waterId: this.waterId(), batchId: this.batchId() }),
    stream: ({ params }) =>
      this.mediaService
        .getBatchMedia(params.waterId, params.batchId)
        .pipe(showGenericErrorToast(this.toastService, () => [] as WaterUserMediaItem[])),
  });

  readonly media = toSignal(
    toObservable(this.mediaResource.value).pipe(filter((v): v is WaterUserMediaItem[] => v !== undefined)),
    { initialValue: [] as WaterUserMediaItem[] },
  );

  readonly isLoading = computed(() => this.mediaResource.isLoading());
  readonly approvedCount = computed(() => this.media().filter((m) => m.approved).length);
  readonly notApprovedCount = computed(() => this.media().filter((m) => !m.approved).length);
  readonly filtered = computed(() =>
    this.media().filter((m) => (this.selectedApproval() === 'approved' ? m.approved : !m.approved)),
  );

  onApprovalChange(approval: ApprovalFilter): void {
    this.selectedApproval.set(approval);
  }

  isVideo(item: WaterUserMediaItem): boolean {
    return !!item.videoUrl;
  }

  /** Thumbnail src: the photo for images, the YouTube thumbnail for videos. */
  thumbUrl(item: WaterUserMediaItem): string | null {
    if (this.isVideo(item)) {
      const id = extractYoutubeId(item.videoUrl);
      return id ? youtubeThumbnailUrl(id) : null;
    }
    return item.photo;
  }

  openLightbox(item: WaterUserMediaItem): void {
    if (this.isVideo(item)) {
      const youtubeId = extractYoutubeId(item.videoUrl);
      if (!youtubeId) return;
      this.dialog.open(LightboxComponent, {
        data: { type: 'video', youtubeId, caption: item.caption ?? undefined },
        maxWidth: '90vw',
        width: '960px',
      });
      return;
    }
    const url = item.photoFull ?? item.photo;
    if (!url) return;
    this.dialog.open(LightboxComponent, {
      data: { type: 'image', url, caption: item.caption ?? undefined },
      maxWidth: '90vw',
    });
  }

  toggleHighlight(item: WaterUserMediaItem): void {
    this.runMutation(this.mediaService.toggleHighlight(this.waterId(), item.id), 'waters.media.highlight.failed');
  }

  approve(item: WaterUserMediaItem): void {
    this.runMutation(this.mediaService.approve(this.waterId(), item.id), 'waters.media.approve.failed');
  }

  disapprove(item: WaterUserMediaItem): void {
    this.runMutation(this.mediaService.disapprove(this.waterId(), item.id), 'waters.media.approve.failed');
  }

  edit(item: WaterUserMediaItem): void {
    const ref = this.dialog.open(WaterUserMediaEditFormComponent, {
      data: { waterId: this.waterId(), mediaId: item.id },
      width: '640px',
      maxWidth: '95vw',
    });
    ref.afterClosed().subscribe((updated) => {
      if (updated) {
        this.toastService.showSuccess(this.translate.instant('waters.media.edit.save-success'));
        this.mediaResource.reload();
      }
    });
  }

  async delete(item: WaterUserMediaItem): Promise<void> {
    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.media.delete.title'),
      prompt: this.translate.instant('waters.media.delete.prompt'),
    });
    if (!confirmed) return;

    try {
      await firstValueFrom(this.mediaService.deleteMedia(this.waterId(), item.id));
      this.toastService.showSuccess(this.translate.instant('waters.media.delete.success'));
      this.mediaResource.reload();
    } catch {
      this.toastService.showError(this.translate.instant('waters.media.delete.failed'));
    }
  }

  /** Runs a media mutation that returns the updated item, then refreshes the grid. */
  private runMutation(request: Observable<WaterUserMediaItem>, errorKey: string): void {
    request.subscribe({
      next: () => this.mediaResource.reload(),
      error: () => this.toastService.showError(this.translate.instant(errorKey)),
    });
  }
}
